import cn from "classnames"
import React, { useEffect, useRef, useState } from "react"
import { type BlockProps } from "."
import { graphql, getFragment } from "@/api"
import { ChevronLeftLarge, ChevronRightLarge } from "@/skylticons"

export const SlidingCarouselFragment = graphql(`
  fragment FrontpagesSlidingCarousel on FrontpagesSlidingCarousel {
    id
    slidingCarouselItems {
      id
      title
      body
      url
      sortOrder

      image {
        data
        width
        height
        pngDefault
        pngTwox
        webpDefault
        webpTwox
      }
    }
  }
`)

const SlidingCarouselBlock: React.FC<BlockProps<typeof SlidingCarouselFragment>> = ({ blockable }) => {
  const block = getFragment(SlidingCarouselFragment, blockable)
  const [activeIndex, setActiveIndex] = useState(0)
  const carouselRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])

  const items = block.slidingCarouselItems

  const [progressLeft, setProgressLeft] = useState(0)
  const [progressWidth, setProgressWidth] = useState(0)
  const [carouselIsOverflowing, setCarouselIsOverflowing] = useState(false)

  const [isAtStart, setIsAtStart] = useState(true)
  const [isAtEnd, setIsAtEnd] = useState(false)

  useEffect(() => {
    const carousel = carouselRef.current
    if (carousel) {
      carousel.addEventListener("scroll", handleScroll)

      setProgressWidth((carousel.clientWidth / carousel.scrollWidth) * 100)
      setProgressLeft((carousel.scrollLeft / carousel.scrollWidth) * 100)

      if (carousel.scrollWidth > carousel.clientWidth) {
        setCarouselIsOverflowing(true)
      }

      const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth
      setIsAtStart(carousel.scrollLeft <= 0)
      setIsAtEnd(carousel.scrollLeft >= maxScrollLeft)
    }

    window.addEventListener("resize", updateCarouselOverflow)
    handleScroll()

    return () => {
      if (carousel) {
        carousel.removeEventListener("scroll", handleScroll)
      }

      window.removeEventListener("resize", updateCarouselOverflow)
    }
  }, [])

  const updateCarouselOverflow = () => {
    const carousel = carouselRef.current

    if (carousel) {
      setProgressWidth((carousel.clientWidth / carousel.scrollWidth) * 100)
      setProgressLeft((carousel.scrollLeft / carousel.scrollWidth) * 100)
      setCarouselIsOverflowing(carousel.scrollWidth > carousel.clientWidth)

      const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth
      setIsAtStart(carousel.scrollLeft <= 0)
      setIsAtEnd(carousel.scrollLeft >= maxScrollLeft)
    }
  }

  const scrollToIndex = (index: number) => {
    if (itemRefs.current[index]) {
      itemRefs.current[index].scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      })
    }
  }

  const handleScroll = () => {
    const carousel = carouselRef.current

    if (carousel) {
      const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth
      setIsAtStart(carousel.scrollLeft <= 0)
      setIsAtEnd(carousel.scrollLeft >= maxScrollLeft)

      const carouselScrollLeft = carousel.scrollLeft
      const carouselWidth = carousel.offsetWidth
      const carouselCenter = carouselScrollLeft + carouselWidth / 2

      let minDistance = Infinity
      let closestIndex = 0
      itemRefs.current.forEach((item, index) => {
        if (item) {
          const itemOffsetLeft = item.offsetLeft
          const itemWidth = item.offsetWidth
          const itemCenter = itemOffsetLeft + itemWidth / 2
          const distance = Math.abs(carouselCenter - itemCenter)

          if (distance < minDistance) {
            minDistance = distance
            closestIndex = index
          }
        }
      })

      setActiveIndex(closestIndex)

      setProgressLeft((carousel.scrollLeft / carousel.scrollWidth) * 100)
      setProgressWidth((carousel.clientWidth / carousel.scrollWidth) * 100)
    }
  }

  return (
    <div className="group/carouselList w-full px-0 lg:container md:mx-auto" role="region" aria-label="Sliding carousel">
      <div className="carousel relative">
        <div className="hidden md:block">
          <div className="hidden onlyHover:block">
            {!isAtStart && (
              <button
                className={
                  "button button-secondary button-medium icon-only raised absolute left-0 mb-0 -translate-x-1/2 translate-y-16 onlyHover:opacity-50 group-hover/carouselList:onlyHover:opacity-100"
                }
                type="button"
                onClick={() => scrollToIndex(activeIndex - 1)}
              >
                <ChevronLeftLarge className="h-10 w-10" />
              </button>
            )}
            {!isAtEnd && (
              <button
                className={
                  "button button-secondary button-medium icon-only raised absolute right-0 mb-0 translate-x-1/2 translate-y-16 onlyHover:opacity-50 group-hover/carouselList:onlyHover:opacity-100"
                }
                type="button"
                onClick={() => scrollToIndex(activeIndex + 1)}
              >
                <ChevronRightLarge className="h-10 w-10" />
              </button>
            )}
          </div>
        </div>

        <div
          role="listbox"
          aria-live="polite"
          ref={carouselRef}
          className="container-scroll-margin scrollbar-hide flex items-baseline gap-16 overflow-x-auto px-[50%] xs:snap-x xs:snap-mandatory md:snap-none md:justify-between md:overflow-x-scroll md:px-0 md:before:hidden md:after:hidden"
        >
          {items.map((item, index) => (
            <div
              key={`carousel-item-${index}`}
              ref={el => (itemRefs.current[index] = el)}
              role="option"
              className={cn(
                "carousel-item my-0 flex min-w-[235px] max-w-[235px] flex-col p-0 transition-all duration-300 ease-in-out xs:snap-center sm:mx-auto sm:flex-none md:mx-0",
                {
                  "opacity-100": activeIndex === index,
                  "opacity-50 md:opacity-100": activeIndex !== index,
                  "cursor-pointer": item.url,
                }
              )}
            >
              <a
                href={item.url}
                className={cn("block", {
                  "pointer-events-none": !item.url,
                })}
              >
                <img
                  src={item.image?.pngDefault}
                  alt={item.title}
                  className={cn(
                    "carousel-item-image mx-auto mb-4 h-auto max-h-[72px] w-auto max-w-full xs:max-w-none md:overflow-x-scroll"
                  )}
                />
              </a>

              <a
                href={item.url}
                className={cn("block", {
                  "pointer-events-none": !item.url,
                })}
              >
                <h5 role="heading" className="heading-five mx-auto mb-2 mt-0 cursor-pointer text-center">
                  {item.title}
                </h5>
              </a>
              <a
                href={item.url}
                className={cn("block", {
                  "pointer-events-none": !item.url,
                })}
              >
                <p className="paragraph-xs mx-auto my-0 w-full text-center text-black">{item.body}</p>
              </a>
            </div>
          ))}
        </div>
      </div>
      <div className="carousel-dots mt-8 flex justify-center md:hidden">
        {items.map((_, index) => (
          <button
            type="button"
            key={`carousel-dot-${index}`}
            onClick={() => scrollToIndex(index)}
            className={cn(
              "mx-2 h-2.5 w-2.5 rounded-full",
              { "bg-ink-50": activeIndex === index },
              { "bg-white-10": activeIndex !== index }
            )}
            aria-label={`Go to slide ${index + 1}`}
          ></button>
        ))}
      </div>
      <div className={cn(carouselIsOverflowing ? "visible" : "hidden", "lg:onlyHover:hidden")}>
        <div className="container mt-2 hidden py-3 md:block">
          <div className="relative h-0.5 overflow-hidden rounded-full bg-white-20">
            <div
              className="absolute top-0 h-0.5 overflow-hidden rounded-full bg-ink-100"
              style={{ left: `${progressLeft || 0}%`, width: `${progressWidth || 0}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SlidingCarouselBlock
